import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  buildIntegratedDashboardStats,
  DashboardFilters,
  DashboardPaymentMeta,
  DashboardPeriod,
  DashboardStatusAmount,
  DashboardStatusFilter,
  getDashboardDateRange,
} from "./adminDashboardStats";

type DashboardView =
  | "OVERVIEW"
  | "CONTRACTS"
  | "PAYMENTS"
  | "MANAGERS"
  | "EXCEPTIONS";

type Props = {
  contracts?: Array<Record<string, unknown>>;
  payments?: Array<Record<string, unknown>>;
  paymentMeta?: DashboardPaymentMeta;
  paymentLoading?: boolean;
  paymentError?: string;
  onPaymentRangeChange?: (range: { dateFrom: string; dateTo: string }) => void;
  onReloadPayments?: (range: { dateFrom: string; dateTo: string }) => void;
  onOpenContractList?: () => void;
  onOpenPaymentList?: () => void;
  now?: Date;
};

const VIEW_OPTIONS: Array<{
  value: DashboardView;
  label: string;
  description: string;
  markerClass: string;
  activeClass: string;
  inactiveClass: string;
  headerClass: string;
  eyebrowClass: string;
}> = [
  {
    value: "OVERVIEW",
    label: "통합현황",
    description: "계약과 결제의 전체 흐름을 요약합니다.",
    markerClass: "bg-violet-500",
    activeClass: "border-violet-700 bg-violet-700 text-white shadow-sm",
    inactiveClass: "border-violet-100 bg-violet-50 text-violet-900 hover:bg-violet-100",
    headerClass: "border-t-violet-500",
    eyebrowClass: "text-violet-700",
  },
  {
    value: "CONTRACTS",
    label: "전자계약",
    description: "계약 진행 상태와 완료 현황을 확인합니다.",
    markerClass: "bg-sky-500",
    activeClass: "border-sky-700 bg-sky-700 text-white shadow-sm",
    inactiveClass: "border-sky-100 bg-sky-50 text-sky-900 hover:bg-sky-100",
    headerClass: "border-t-sky-500",
    eyebrowClass: "text-sky-700",
  },
  {
    value: "PAYMENTS",
    label: "링크결제",
    description: "결제 요청, 승인, 미결제와 만료 상태를 확인합니다.",
    markerClass: "bg-emerald-500",
    activeClass: "border-emerald-700 bg-emerald-700 text-white shadow-sm",
    inactiveClass: "border-emerald-100 bg-emerald-50 text-emerald-900 hover:bg-emerald-100",
    headerClass: "border-t-emerald-500",
    eyebrowClass: "text-emerald-700",
  },
  {
    value: "MANAGERS",
    label: "담당자·파트",
    description: "담당자별 운영 상태와 확인 필요 건을 비교합니다.",
    markerClass: "bg-amber-500",
    activeClass: "border-amber-600 bg-amber-600 text-white shadow-sm",
    inactiveClass: "border-amber-100 bg-amber-50 text-amber-950 hover:bg-amber-100",
    headerClass: "border-t-amber-500",
    eyebrowClass: "text-amber-700",
  },
  {
    value: "EXCEPTIONS",
    label: "오류·예외",
    description: "확인이 필요한 오류와 데이터 예외를 모아 봅니다.",
    markerClass: "bg-rose-500",
    activeClass: "border-rose-700 bg-rose-700 text-white shadow-sm",
    inactiveClass: "border-rose-100 bg-rose-50 text-rose-900 hover:bg-rose-100",
    headerClass: "border-t-rose-500",
    eyebrowClass: "text-rose-700",
  },
];

const PERIOD_OPTIONS: Array<{ value: DashboardPeriod; label: string }> = [
  { value: "TODAY", label: "오늘" },
  { value: "7D", label: "최근 7일" },
  { value: "MONTH", label: "이번 달" },
  { value: "LAST_MONTH", label: "지난달" },
  { value: "CUSTOM", label: "직접선택" },
];

const STATUS_OPTIONS: Array<{ value: DashboardStatusFilter; label: string }> = [
  { value: "ALL", label: "전체 상태" },
  { value: "NORMAL", label: "정상" },
  { value: "IN_PROGRESS", label: "진행중" },
  { value: "DELAYED", label: "지연" },
  { value: "ERROR", label: "오류" },
];

const CONTRACT_STATUS_LABELS: Record<string, string> = {
  COMPLETED: "계약 완료",
  PENDING_COREP: "공동대표 대기",
  IN_PROGRESS: "확인 진행중",
  REJECTED: "관리자 반려",
  ERROR: "오류",
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  WAITING_CUSTOMER: "결제 대기",
  APPROVED: "승인 완료",
  PARTIALLY_CANCELED: "부분취소",
  CANCELED: "취소 완료",
  FAILED: "승인 실패",
  DRAFT: "링크 생성 전",
  PROCESSING: "확인 필요",
};

const AGING_LABELS: Record<string, string> = {
  TODAY: "당일~1일",
  "2_3_DAYS": "2~3일",
  "4_7_DAYS": "4~7일",
  "8_PLUS_DAYS": "8일 이상",
};

const DATA_AVAILABILITY = [
  ["계약 완료 건수·완료일", "AVAILABLE", "contractStatus / completedAt"],
  ["전자공급계약서 완료율", "DERIVABLE", "삭제 제외 contracts 문서 기준"],
  ["본인확인·서명 단계", "PARTIAL", "제출된 계약 증빙만 보유"],
  ["공동대표 인증 대기", "DERIVABLE", "coRepresentativeWorkflow"],
  ["결제 링크 발급 건수·금액", "DERIVABLE", "유효 BUYLINK / STANDARD"],
  ["승인액·취소액·순승인액", "DERIVABLE", "KCP 승인·취소 snapshot"],
  ["기간별 취소 발생액", "PARTIAL", "현재 화면은 선택 기간 승인 건의 누적 취소"],
  ["신규 BUYLINK 유효기간", "AVAILABLE", "저장된 KCP url_expire_dt"],
  ["과거 BUYLINK 유효기간", "PARTIAL", "기존 문서는 만료일 필드가 없을 수 있음"],
  ["링크 열람", "NOT_AVAILABLE", "열람 이벤트 미수집"],
  ["실제 입금·정산액", "NOT_AVAILABLE", "KCP 승인과 정산/입금은 별도"],
  ["담당자·파트별 결제", "PARTIAL", "결제 요청자와 영업 담당자 의미가 다름"],
  ["계약→결제 전환", "PARTIAL", "사업자번호 일치 거래처만 연결"],
] as const;

const countFormatter = new Intl.NumberFormat("ko-KR");
const refreshedTimeFormatter = new Intl.DateTimeFormat("ko-KR", {
  hour: "2-digit",
  minute: "2-digit",
});

type MetricTone = "blue" | "green" | "amber" | "red" | "slate";

const METRIC_TONE_STYLES: Record<
  MetricTone,
  { frame: string; accent: string; label: string; value: string }
> = {
  blue: {
    frame: "border-sky-100 bg-white",
    accent: "bg-sky-500",
    label: "text-sky-800",
    value: "text-sky-950",
  },
  green: {
    frame: "border-emerald-100 bg-white",
    accent: "bg-emerald-500",
    label: "text-emerald-800",
    value: "text-emerald-950",
  },
  amber: {
    frame: "border-amber-100 bg-white",
    accent: "bg-amber-500",
    label: "text-amber-800",
    value: "text-amber-950",
  },
  red: {
    frame: "border-rose-100 bg-white",
    accent: "bg-rose-500",
    label: "text-rose-800",
    value: "text-rose-950",
  },
  slate: {
    frame: "border-slate-200 bg-white",
    accent: "bg-slate-400",
    label: "text-slate-600",
    value: "text-slate-950",
  },
};

const getStatusBarClass = (key: string) => {
  if (/PARTIALLY_CANCELED/.test(key)) return "bg-sky-600";
  if (/ERROR|FAILED|REJECTED|CANCELED/.test(key)) return "bg-rose-600";
  if (/PENDING|WAITING|DRAFT/.test(key)) return "bg-amber-500";
  if (/APPROVED|COMPLETED/.test(key)) return "bg-emerald-600";
  if (/PROCESSING|IN_PROGRESS|PARTIALLY/.test(key)) return "bg-sky-600";
  return "bg-slate-500";
};

const formatCount = (value: number) => `${countFormatter.format(value)}건`;

const formatWon = (value: number) => `${countFormatter.format(value)}원`;

const formatWonCompact = (value: number) => {
  const sign = value < 0 ? "-" : "";
  const absolute = Math.abs(value);
  if (absolute >= 100_000_000) {
    const eok = Math.floor(absolute / 100_000_000);
    const man = Math.floor((absolute % 100_000_000) / 10_000);
    return `${sign}${eok}억${man > 0 ? ` ${countFormatter.format(man)}만` : ""}원`;
  }
  if (absolute >= 10_000) {
    return `${sign}${countFormatter.format(Math.floor(absolute / 10_000))}만원`;
  }
  return formatWon(value);
};

const getPaymentErrorMessage = (error: string) => {
  const normalized = String(error || "").toLowerCase();
  if (normalized.includes("permission-denied")) {
    return "결제 통계 조회 권한이 없습니다. 본사 관리자 권한을 확인해 주세요.";
  }
  if (normalized.includes("failed-precondition")) {
    return "결제 통계 조회 조건을 처리하지 못했습니다. Firestore 조회 구성을 확인해 주세요.";
  }
  return error || "결제 데이터를 불러오지 못했습니다.";
};

const MetricCard = ({
  label,
  value,
  detail,
  tone = "slate",
}: {
  label: string;
  value: string;
  detail: string;
  tone?: MetricTone;
}) => {
  const toneStyle = METRIC_TONE_STYLES[tone];
  return (
    <div
      className={`relative min-w-0 overflow-hidden rounded-2xl border p-4 shadow-sm transition-shadow hover:shadow-md ${toneStyle.frame}`}
    >
      <span className={`absolute inset-x-0 top-0 h-1 ${toneStyle.accent}`} />
      <p className={`text-xs font-extrabold tracking-wide ${toneStyle.label}`}>{label}</p>
      <p
        className={`mt-2 break-words text-2xl font-black leading-tight tabular-nums sm:text-3xl ${toneStyle.value}`}
      >
        {value}
      </p>
      <p className="mt-2 break-keep text-xs font-semibold leading-5 text-slate-600">
        {detail}
      </p>
    </div>
  );
};

const SectionCard = ({
  title,
  description,
  children,
  action,
  className = "",
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) => (
  <section
    className={`rounded-3xl border border-slate-200/80 bg-white p-4 shadow-sm sm:p-5 ${className}`}
  >
    <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h4 className="text-base font-black text-slate-950 sm:text-lg">{title}</h4>
        {description ? (
          <p className="mt-1 break-keep text-xs font-semibold leading-5 text-slate-600">
            {description}
          </p>
        ) : null}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const StatusBars = ({
  rows,
  total,
  showAmount = false,
}: {
  rows: Array<{ key: string; label: string; value: number; amount?: number }>;
  total: number;
  showAmount?: boolean;
}) => (
  <div className="space-y-4">
    {rows.map((row) => {
      const percent = total > 0 ? Math.round((row.value / total) * 1000) / 10 : 0;
      return (
        <div key={row.key}>
          <div className="mb-1.5 flex items-end justify-between gap-3 text-sm">
            <span className="font-bold text-slate-700">{row.label}</span>
            <span className="shrink-0 text-right font-black tabular-nums text-slate-950">
              {formatCount(row.value)} · {percent}%
              {showAmount ? (
                <span className="ml-2 text-xs text-slate-500">
                  {formatWonCompact(row.amount || 0)}
                </span>
              ) : null}
            </span>
          </div>
          <div
            className="h-2.5 overflow-hidden rounded-full bg-slate-100"
            role="progressbar"
            aria-label={`${row.label} ${formatCount(row.value)} ${percent}%`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
          >
            <div
              className={`h-full rounded-full ${getStatusBarClass(row.key)}`}
              style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
            />
          </div>
        </div>
      );
    })}
  </div>
);

const PaymentAgingTable = ({ aging }: { aging: Record<string, DashboardStatusAmount> }) => (
  <div
    className="overflow-x-auto overscroll-x-contain rounded-2xl border border-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2"
    role="region"
    aria-label="미결제 경과기간 표"
    tabIndex={0}
  >
    <table className="min-w-[620px] w-full text-left text-sm">
      <caption className="sr-only">미결제 요청의 경과기간별 건수, 금액과 상태</caption>
      <thead className="sticky top-0 z-10 bg-slate-50">
        <tr className="border-b border-slate-200 text-xs font-black text-slate-600">
          <th scope="col" className="px-3 py-3">경과기간</th>
          <th scope="col" className="px-3 py-3 text-right">건수</th>
          <th scope="col" className="px-3 py-3 text-right">금액</th>
          <th scope="col" className="px-3 py-3">상태</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(AGING_LABELS).map(([key, label]) => {
          const row = aging[key] || { count: 0, amount: 0 };
          return (
            <tr
              key={key}
              className="border-b border-slate-100 odd:bg-white even:bg-slate-50/60 hover:bg-sky-50/70 last:border-0"
            >
              <td className="px-3 py-3 font-black text-slate-800">{label}</td>
              <td className="px-3 py-3 text-right font-bold tabular-nums">{formatCount(row.count)}</td>
              <td className="px-3 py-3 text-right font-black tabular-nums">
                {formatWonCompact(row.amount)}
              </td>
              <td className="px-3 py-3">
                <span
                  className={`rounded-full px-2 py-1 text-xs font-black ${
                    key === "8_PLUS_DAYS"
                      ? "bg-red-100 text-red-800"
                      : key === "4_7_DAYS"
                      ? "bg-amber-100 text-amber-800"
                      : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {key === "8_PLUS_DAYS" ? "우선 확인" : "대기"}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default function AdminIntegratedDashboard({
  contracts = [],
  payments = [],
  paymentMeta = {
    loaded: false,
    truncated: false,
    total: 0,
    dateFrom: "",
    dateTo: "",
  },
  paymentLoading = false,
  paymentError = "",
  onPaymentRangeChange,
  onReloadPayments,
  onOpenContractList,
  onOpenPaymentList,
  now,
}: Props) {
  const [dashboardNow, setDashboardNow] = useState(() => now ?? new Date());
  const paymentRangeChangeRef = useRef(onPaymentRangeChange);
  paymentRangeChangeRef.current = onPaymentRangeChange;
  const initialRange = getDashboardDateRange("MONTH", dashboardNow);
  const [view, setView] = useState<DashboardView>("OVERVIEW");
  const [filters, setFilters] = useState<DashboardFilters>({
    period: "MONTH",
    ...initialRange,
    corporation: "ALL",
    manager: "ALL",
    part: "ALL",
    status: "ALL",
  });

  useEffect(() => {
    paymentRangeChangeRef.current?.({
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
    });
  }, [filters.dateFrom, filters.dateTo]);

  const dashboard = useMemo(
    () =>
      buildIntegratedDashboardStats({
        contracts,
        payments,
        filters,
        now: dashboardNow,
      }),
    [contracts, dashboardNow, filters, payments]
  );

  const updatePeriod = (period: DashboardPeriod) => {
    const range = getDashboardDateRange(
      period,
      dashboardNow,
      filters.dateFrom,
      filters.dateTo
    );
    setFilters((current) => ({ ...current, period, ...range }));
  };

  const updateCustomDate = (key: "dateFrom" | "dateTo", value: string) => {
    setFilters((current) => ({ ...current, period: "CUSTOM", [key]: value }));
  };

  const resetFilters = () => {
    const range = getDashboardDateRange("MONTH", dashboardNow);
    setFilters({
      period: "MONTH",
      ...range,
      corporation: "ALL",
      manager: "ALL",
      part: "ALL",
      status: "ALL",
    });
  };

  const reloadDashboardPayments = () => {
    setDashboardNow(now ?? new Date());
    onReloadPayments?.({ dateFrom: filters.dateFrom, dateTo: filters.dateTo });
  };

  const summary = dashboard.summary;
  const contractTotal = Object.values(dashboard.contractStatus).reduce(
    (sum, count) => sum + count,
    0
  );
  const paymentTotal = Object.values(dashboard.paymentStatus).reduce(
    (sum, row) => sum + row.count,
    0
  );
  const maxContractTrend = Math.max(
    1,
    ...dashboard.trends.map((row) => row.contractCompletedCount)
  );
  const maxPaymentTrend = Math.max(
    1,
    ...dashboard.trends.map((row) => row.paymentApprovedAmount)
  );
  const hasDashboardData =
    summary.submittedContractCount > 0 ||
    summary.completedContractCount > 0 ||
    summary.paymentRequestCount > 0 ||
    summary.paymentApprovedCount > 0 ||
    summary.paymentExpiringTodayCount > 0 ||
    summary.paymentExpiredCount > 0;
  const paymentMetricsUnavailable = filters.part !== "ALL";
  const paymentValuePrefix = paymentMeta.truncated ? "반환범위 " : "";
  const displayPaymentMoney = (value: number) =>
    paymentMetricsUnavailable ? "—" : `${paymentValuePrefix}${formatWonCompact(value)}`;
  const displayPaymentCount = (value: number) =>
    paymentMetricsUnavailable ? "—" : `${paymentValuePrefix}${formatCount(value)}`;
  const paymentTrendComplete =
    paymentMeta.loaded &&
    !paymentMeta.truncated &&
    Boolean(dashboard.trends[0]?.date) &&
    paymentMeta.dateFrom <= dashboard.trends[0].date &&
    paymentMeta.dateTo >= dashboard.trends[dashboard.trends.length - 1].date;
  const activeView =
    VIEW_OPTIONS.find((option) => option.value === view) || VIEW_OPTIONS[0];
  const activePeriodLabel =
    PERIOD_OPTIONS.find((option) => option.value === filters.period)?.label || "직접선택";
  const activeStatusLabel =
    STATUS_OPTIONS.find((option) => option.value === filters.status)?.label || "전체 상태";
  const refreshedAtLabel = refreshedTimeFormatter.format(dashboardNow);
  const actionTypeCount = dashboard.actions.length;

  const renderPaymentState = () => {
    if (paymentLoading) {
      return (
        <div
          className="rounded-2xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm font-bold text-sky-900 shadow-sm"
          role="status"
          aria-live="polite"
        >
          링크결제 데이터를 불러오는 중입니다.
        </div>
      );
    }
    if (paymentError) {
      return (
        <div
          className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-900 shadow-sm"
          role="alert"
        >
          {getPaymentErrorMessage(paymentError)}
        </div>
      );
    }
    if (paymentMeta.truncated) {
      return (
        <div
          className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-bold text-amber-950 shadow-sm"
          role="status"
          aria-live="polite"
        >
          조회 상한 5,000건에 도달했습니다. 결제 KPI는 전체가 아닌 반환 범위 기준입니다.
        </div>
      );
    }
    return null;
  };

  const renderOverview = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 min-[390px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        <MetricCard
          label="전자공급계약서 완료율"
          value={
            summary.contractCompletionRate === null
              ? "—"
              : `${summary.contractCompletionRate}%`
          }
          detail={`${formatCount(summary.completedContractCount)} / ${formatCount(
            summary.contractTargetCount
          )} · 삭제 제외 계약서 기준`}
          tone="blue"
        />
        <MetricCard
          label="오늘 계약 완료"
          value={formatCount(summary.todayCompletedContractCount)}
          detail={`선택 조건 내 오늘 완료 · 제출 ${formatCount(summary.submittedContractCount)}`}
          tone="blue"
        />
        <MetricCard
          label="계약 정체(확인 가능)"
          value={formatCount(summary.stalledContractCount)}
          detail="제출 후 3일 이상 미완료 계약 기준"
          tone={summary.stalledContractCount > 0 ? "amber" : "slate"}
        />
        <MetricCard
          label="결제 요청액"
          value={displayPaymentMoney(summary.paymentRequestAmount)}
          detail={
            paymentMetricsUnavailable
              ? "결제에 파트 연결 필드가 없어 집계하지 않습니다."
              : `유효 링크 ${displayPaymentCount(summary.paymentRequestCount)} · 요청일 기준`
          }
          tone="slate"
        />
        <MetricCard
          label="결제 순승인액"
          value={displayPaymentMoney(summary.paymentNetApprovedAmount)}
          detail={
            paymentMetricsUnavailable
              ? "결제에 파트 연결 필드가 없어 집계하지 않습니다."
              : `선택 기간 승인 ${formatWonCompact(
                  summary.paymentApprovedAmount
                )} - 현재 누적 취소 ${formatWonCompact(
                  summary.paymentCancelledAmount
                )}`
          }
          tone="green"
        />
        <MetricCard
          label="미결제"
          value={displayPaymentMoney(summary.paymentOutstandingAmount)}
          detail={
            paymentMetricsUnavailable
              ? "결제에 파트 연결 필드가 없어 집계하지 않습니다."
              : `유효 링크 중 미승인 ${displayPaymentCount(
                  summary.paymentOutstandingCount
                )}`
          }
          tone={summary.paymentOutstandingCount > 0 ? "red" : "slate"}
        />
        <MetricCard
          label="오늘 만료 BUYLINK"
          value={displayPaymentMoney(summary.paymentExpiringTodayAmount)}
          detail={
            paymentMetricsUnavailable
              ? "결제에 파트 연결 필드가 없어 집계하지 않습니다."
              : `${displayPaymentCount(summary.paymentExpiringTodayCount)} · 미승인 현재 상태`
          }
          tone={summary.paymentExpiringTodayCount > 0 ? "amber" : "slate"}
        />
        <MetricCard
          label="만료 BUYLINK"
          value={displayPaymentMoney(summary.paymentExpiredAmount)}
          detail={
            paymentMetricsUnavailable
              ? "결제에 파트 연결 필드가 없어 집계하지 않습니다."
              : `${displayPaymentCount(summary.paymentExpiredCount)} · 오늘 이전 만료`
          }
          tone={summary.paymentExpiredCount > 0 ? "red" : "slate"}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(320px,0.7fr)]">
        <SectionCard
          title="계약 → 결제 연결 흐름"
          description="사업자번호가 일치하는 고유 거래처 기준입니다. 결제 건수와 거래처 수를 혼동하지 않습니다."
          className="order-2 xl:order-1"
        >
          <p className="mb-2 text-xs font-semibold text-slate-500 md:hidden">
            좌우로 밀어 전체 단계를 확인할 수 있습니다.
          </p>
          <div
            className="overflow-x-auto overscroll-x-contain pb-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
            role="region"
            aria-label="전자계약부터 결제 승인까지 연결 흐름"
            tabIndex={0}
          >
            <div className="grid min-w-[780px] grid-cols-6 gap-2">
              {dashboard.funnel.map((stage, index) => (
                <div key={stage.key} className="relative rounded-2xl border border-slate-200 bg-slate-50 p-3 text-center">
                  <p className="text-xs font-black text-slate-500">{stage.label}</p>
                  <p className="mt-2 text-2xl font-black text-slate-950">
                    {countFormatter.format(stage.count)}
                  </p>
                  {index > 0 ? (
                    <p className="mt-2 text-xs font-semibold text-slate-600">
                      직전 대비 {stage.previousConversionRate === null ? "—" : `${stage.previousConversionRate}%`}
                    </p>
                  ) : (
                    <p className="mt-2 text-xs font-semibold text-slate-600">기준 단계</p>
                  )}
                </div>
              ))}
            </div>
          </div>
          <p className="mt-3 text-xs font-bold leading-5 text-slate-500">
            계약 범위는 전자공급계약서 데이터로 한정합니다. 사업자번호 누락 계약 {formatCount(
              dashboard.diagnostics.contractMissingBusinessNumberCount
            )}, 결제 {formatCount(dashboard.diagnostics.paymentMissingBusinessNumberCount)}는 연결 흐름에서 제외했습니다.
          </p>
        </SectionCard>

        <SectionCard
          title="오늘 조치 필요"
          description="조회 전용입니다. 기존 계약·결제 목록에서 후속 확인합니다."
          className="order-1 border-rose-100 bg-rose-50/30 xl:order-2"
        >
          {dashboard.actions.length > 0 ? (
            <div className="space-y-3">
              {dashboard.actions.slice(0, 5).map((action) => (
                <div
                  key={action.key}
                  className={`rounded-2xl border bg-white p-3 shadow-sm ${
                    action.severity === "URGENT" ? "border-rose-200" : "border-amber-200"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <span
                        className={`inline-flex rounded-full px-2 py-1 text-[11px] font-black ${
                          action.severity === "URGENT"
                            ? "bg-red-100 text-red-800"
                            : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {action.severity === "URGENT" ? "긴급" : "확인 필요"}
                      </span>
                      <p className="mt-2 break-words font-black text-slate-900">{action.title}</p>
                    </div>
                    <div className="shrink-0 text-right tabular-nums">
                      <p className="text-xl font-black text-slate-950">{formatCount(action.count)}</p>
                      {action.amount > 0 ? (
                        <p className="text-xs font-bold text-slate-500">{formatWonCompact(action.amount)}</p>
                      ) : null}
                    </div>
                  </div>
                  <p className="mt-2 break-keep text-xs font-bold leading-5 text-slate-500">{action.detail}</p>
                  <button
                    type="button"
                    onClick={action.scope === "PAYMENT" ? onOpenPaymentList : onOpenContractList}
                    className="mt-3 min-h-[44px] w-full rounded-xl bg-slate-900 px-3 py-2 text-xs font-black text-white transition hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-700 focus-visible:ring-offset-2"
                  >
                    기존 목록 보기
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm font-bold text-slate-500">
              선택한 조건에서 즉시 확인할 항목이 없습니다.
            </div>
          )}
        </SectionCard>
      </div>

      <SectionCard
        title="최근 30일 추이"
        description={`계약 완료 건수와 결제 순승인액을 서로 다른 축으로 분리했습니다. 결제 추이: ${
          paymentTrendComplete ? "30일 조회 범위 충족" : "현재 결제 조회 범위 내 부분 데이터"
        }.`}
      >
        <p className="mb-2 text-xs font-semibold text-slate-500 md:hidden">
          좌우로 밀어 30일 전체 추이를 확인할 수 있습니다.
        </p>
        <div
          className="overflow-x-auto overscroll-x-contain pb-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
          role="region"
          aria-label="최근 30일 계약 완료와 결제 순승인액 추이"
          tabIndex={0}
        >
          <div className="min-w-[820px] space-y-5">
            <div>
              <p className="mb-2 text-xs font-black text-blue-700">일별 계약 완료 건수</p>
              <div className="flex h-28 items-end gap-1 border-b border-slate-200">
                {dashboard.trends.map((row, index) => (
                  <div key={row.date} className="flex min-w-0 flex-1 flex-col items-center justify-end gap-1">
                    <div
                      className="w-full rounded-t bg-sky-600"
                      style={{ height: `${row.contractCompletedCount === 0 ? 0 : Math.max(2, (row.contractCompletedCount / maxContractTrend) * 90)}px` }}
                      title={`${row.date}: ${formatCount(row.contractCompletedCount)}`}
                      aria-label={`${row.date} 계약 완료 ${formatCount(row.contractCompletedCount)}`}
                    />
                    <span className="h-4 text-[11px] font-medium text-slate-600">{index % 5 === 0 ? row.date.slice(5) : ""}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-black text-emerald-700">일별 결제 순승인액</p>
              <div className="flex h-28 items-end gap-1 border-b border-slate-200">
                {dashboard.trends.map((row, index) => (
                  <div key={row.date} className="flex min-w-0 flex-1 flex-col items-center justify-end gap-1">
                    <div
                      className="w-full rounded-t bg-emerald-600"
                      style={{ height: `${row.paymentApprovedAmount === 0 ? 0 : Math.max(2, (row.paymentApprovedAmount / maxPaymentTrend) * 90)}px` }}
                      title={`${row.date}: ${formatWon(row.paymentApprovedAmount)}`}
                      aria-label={`${row.date} 결제 순승인액 ${formatWon(row.paymentApprovedAmount)}`}
                    />
                    <span className="h-4 text-[11px] font-medium text-slate-600">{index % 5 === 0 ? row.date.slice(5) : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </SectionCard>
    </div>
  );

  const renderContracts = () => (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
      <SectionCard
        title="전자계약 상세 현황"
        description="제출된 계약 문서 기준입니다. 제출 전 링크 발송·열람 단계는 포함하지 않습니다."
        action={
          <button type="button" onClick={onOpenContractList} className="min-h-[44px] rounded-xl bg-slate-900 px-4 py-2 text-xs font-black text-white">
            계약 목록 보기
          </button>
        }
      >
        <StatusBars
          total={contractTotal}
          rows={Object.entries(dashboard.contractStatus).map(([key, value]) => ({
            key,
            label: CONTRACT_STATUS_LABELS[key] || key,
            value,
          }))}
        />
      </SectionCard>
      <SectionCard title="계약 데이터 경계">
        <dl className="space-y-3 text-sm">
          <div className="rounded-xl bg-blue-50 p-3">
            <dt className="font-black text-blue-900">완료</dt>
            <dd className="mt-1 font-bold text-blue-700">contractStatus=COMPLETED 또는 완료 상태</dd>
          </div>
          <div className="rounded-xl bg-amber-50 p-3">
            <dt className="font-black text-amber-900">정체</dt>
            <dd className="mt-1 font-bold text-amber-700">제출 후 3일 이상 미완료인 확인 가능 계약</dd>
          </div>
          <div className="rounded-xl bg-slate-100 p-3">
            <dt className="font-black text-slate-800">미수집</dt>
            <dd className="mt-1 font-bold text-slate-600">전자공급계약서 생성 전 단계별 체류시간</dd>
          </div>
        </dl>
      </SectionCard>
    </div>
  );

  const renderPayments = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 min-[390px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        <MetricCard label="결제 요청액" value={displayPaymentMoney(summary.paymentRequestAmount)} detail={paymentMetricsUnavailable ? "파트 연결 데이터 미수집" : displayPaymentCount(summary.paymentRequestCount)} />
        <MetricCard label="결제 승인액" value={displayPaymentMoney(summary.paymentApprovedAmount)} detail={paymentMetricsUnavailable ? "파트 연결 데이터 미수집" : displayPaymentCount(summary.paymentApprovedCount)} tone="green" />
        <MetricCard label="승인 건 누적 취소액" value={displayPaymentMoney(summary.paymentCancelledAmount)} detail="선택 기간 승인 건의 현재 KCP 취소 누계" tone="slate" />
        <MetricCard label="승인 건 순승인액" value={displayPaymentMoney(summary.paymentNetApprovedAmount)} detail="선택 기간 승인액 - 현재 누적 취소액" tone="green" />
        <MetricCard label="미결제액" value={displayPaymentMoney(summary.paymentOutstandingAmount)} detail={paymentMetricsUnavailable ? "파트 연결 데이터 미수집" : displayPaymentCount(summary.paymentOutstandingCount)} tone="amber" />
        <MetricCard label="오늘 만료 BUYLINK" value={displayPaymentMoney(summary.paymentExpiringTodayAmount)} detail={paymentMetricsUnavailable ? "파트 연결 데이터 미수집" : displayPaymentCount(summary.paymentExpiringTodayCount)} tone="amber" />
        <MetricCard label="만료 BUYLINK" value={displayPaymentMoney(summary.paymentExpiredAmount)} detail={paymentMetricsUnavailable ? "파트 연결 데이터 미수집" : displayPaymentCount(summary.paymentExpiredCount)} tone="red" />
        <MetricCard label="승인 성공률" value="데이터 미수집" detail="실제 고객 결제 시도 분모가 없습니다." tone="slate" />
      </div>
      <p className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-xs font-bold leading-5 text-amber-900">
        신규 BUYLINK의 저장된 유효일자 기준입니다. 과거 유효일자 누락 미승인 BUYLINK {displayPaymentCount(
          dashboard.diagnostics.paymentMissingExpiryCount
        )}는 오늘 만료·만료 집계에서 제외합니다(PARTIAL). 조회 범위 {paymentMeta.dateFrom || "—"} ~ {paymentMeta.dateTo || "—"}, 최대 5,000건입니다.
      </p>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <SectionCard title="링크결제 상태" description="BUYLINK·STANDARD 유효 링크의 요청일 기준 현재 상태입니다.">
          <StatusBars
            total={paymentTotal}
            showAmount
            rows={Object.entries(dashboard.paymentStatus).map(([key, row]) => ({
              key,
              label: PAYMENT_STATUS_LABELS[key] || key,
              value: row.count,
              amount: row.amount,
            }))}
          />
        </SectionCard>
        <SectionCard
          title="미결제 Aging"
          description="링크 등록일 또는 요청일 경과 기준이며, 공식 BUYLINK 유효일자는 위 만료 카드에서 별도로 봅니다."
          action={
            <button type="button" onClick={onOpenPaymentList} className="min-h-[44px] rounded-xl bg-slate-900 px-4 py-2 text-xs font-black text-white transition hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-700 focus-visible:ring-offset-2">
              결제 목록 보기
            </button>
          }
        >
          <PaymentAgingTable aging={dashboard.paymentAging} />
        </SectionCard>
      </div>
    </div>
  );

  const renderManagers = () => (
    <SectionCard
      title="담당자 운영 현황"
      description="성과 순위가 아닌 운영 확인용입니다. 결제 담당자는 요청자 snapshot 기준이며 파트 연결은 일부 제한됩니다."
    >
      <p className="mb-2 text-xs font-semibold text-slate-500 md:hidden">
        좌우로 밀어 담당자별 전체 지표를 확인할 수 있습니다.
      </p>
      <div
        className="overflow-x-auto overscroll-x-contain rounded-2xl border border-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-2"
        role="region"
        aria-label="담당자 운영 현황 표"
        tabIndex={0}
      >
        <table className="min-w-[920px] w-full text-left text-sm">
          <caption className="sr-only">담당자와 파트별 계약, 결제와 조치 필요 현황</caption>
          <thead className="sticky top-0 z-10 bg-slate-50">
            <tr className="border-b border-slate-200 text-xs font-black text-slate-600">
              <th scope="col" className="px-3 py-3">담당자</th>
              <th scope="col" className="px-3 py-3">파트</th>
              <th scope="col" className="px-3 py-3 text-right">계약 제출</th>
              <th scope="col" className="px-3 py-3 text-right">계약 완료</th>
              <th scope="col" className="px-3 py-3 text-right">결제 순승인액</th>
              <th scope="col" className="px-3 py-3 text-right">미결제액</th>
              <th scope="col" className="px-3 py-3 text-right">조치 필요</th>
            </tr>
          </thead>
          <tbody>
            {dashboard.managers.length > 0 ? (
              dashboard.managers.map((row) => (
                <tr key={row.manager} className="border-b border-slate-100 odd:bg-white even:bg-slate-50/60 hover:bg-amber-50/70 last:border-0">
                  <td className="max-w-[14rem] px-3 py-3">
                    <button
                      type="button"
                      onClick={() => setFilters((current) => ({ ...current, manager: row.manager }))}
                      className="inline-flex min-h-[44px] min-w-[44px] items-center break-words text-left font-black text-sky-700 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2"
                    >
                      {row.manager}
                    </button>
                  </td>
                  <td className="px-3 py-3 font-bold text-slate-600">{row.part}</td>
                  <td className="px-3 py-3 text-right font-bold tabular-nums">{formatCount(row.submittedContracts)}</td>
                  <td className="px-3 py-3 text-right font-bold tabular-nums">{formatCount(row.completedContracts)}</td>
                  <td className="px-3 py-3 text-right font-black tabular-nums">{formatWonCompact(row.paymentApprovedAmount)}</td>
                  <td className="px-3 py-3 text-right font-black tabular-nums text-amber-800">{formatWonCompact(row.paymentOutstandingAmount)}</td>
                  <td className="px-3 py-3 text-right font-black tabular-nums text-rose-700">{formatCount(row.attentionCount)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center font-bold text-slate-500">
                  선택한 조건에 해당하는 담당자 데이터가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );

  const renderExceptions = () => (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <SectionCard title="오류 원인 TOP" description="민감정보 없이 저장된 오류 코드만 집계합니다.">
        {dashboard.exceptions.length > 0 ? (
          <div className="space-y-2">
            {dashboard.exceptions.map((row) => (
              <div key={row.key} className="flex items-center justify-between gap-3 rounded-2xl border border-rose-100 bg-rose-50/40 px-4 py-3 transition hover:bg-rose-50">
                <div className="min-w-0">
                  <p className="break-words [overflow-wrap:anywhere] font-black text-slate-900">{row.label}</p>
                  <p className="mt-1 text-xs font-bold text-slate-500">
                    {row.category === "KCP"
                      ? "외부 KCP 오류"
                      : row.category === "SYSTEM"
                      ? "시스템 오류"
                      : row.category === "SALES"
                      ? "영업 담당자 조치 필요"
                      : "데이터 확인 필요"}
                  </p>
                </div>
                <span className="shrink-0 text-xl font-black tabular-nums text-rose-700">{formatCount(row.count)}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm font-bold text-slate-500">
            선택한 조건에서 집계 가능한 오류 코드가 없습니다.
          </div>
        )}
      </SectionCard>
      <SectionCard title="데이터 신뢰도" description="확인되지 않은 지표는 숫자를 만들지 않습니다.">
        <p className="mb-2 text-xs font-semibold text-slate-500 md:hidden">
          좌우로 밀어 지표별 근거를 확인할 수 있습니다.
        </p>
        <div
          className="overflow-x-auto overscroll-x-contain rounded-2xl border border-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-600 focus-visible:ring-offset-2"
          role="region"
          aria-label="데이터 신뢰도 표"
          tabIndex={0}
        >
          <table className="min-w-[640px] w-full text-left text-xs">
            <caption className="sr-only">대시보드 지표별 데이터 신뢰도와 근거</caption>
            <thead className="sticky top-0 z-10 bg-slate-50">
              <tr className="border-b border-slate-200 font-black text-slate-600">
                <th scope="col" className="px-3 py-3">지표</th>
                <th scope="col" className="px-3 py-3">신뢰도</th>
                <th scope="col" className="px-3 py-3">근거</th>
              </tr>
            </thead>
            <tbody>
              {DATA_AVAILABILITY.map(([metric, reliability, source]) => (
                <tr key={metric} className="border-b border-slate-100 odd:bg-white even:bg-slate-50/60 hover:bg-rose-50/50 last:border-0">
                  <td className="px-3 py-3 font-black text-slate-800">{metric}</td>
                  <td className="px-3 py-3">
                    <span
                      className={`rounded-full px-2 py-1 font-black ${
                        reliability === "AVAILABLE"
                          ? "bg-emerald-100 text-emerald-800"
                          : reliability === "DERIVABLE"
                          ? "bg-blue-100 text-blue-800"
                          : reliability === "PARTIAL"
                          ? "bg-amber-100 text-amber-800"
                          : "bg-slate-200 text-slate-700"
                      }`}
                    >
                      {reliability}
                    </span>
                  </td>
                  <td className="px-3 py-3 font-bold text-slate-500">{source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="admin-integrated-dashboard">
      <section
        className={`overflow-hidden rounded-3xl border border-t-4 border-slate-200 bg-white p-4 shadow-sm sm:p-5 ${activeView.headerClass}`}
      >
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0">
            <p className={`text-xs font-black uppercase tracking-[0.18em] ${activeView.eyebrowClass}`}>
              본사 관리자 현황 · 조회 전용
            </p>
            <h3 className="mt-1 text-2xl font-black leading-tight text-slate-950 sm:text-3xl">
              {activeView.label}
            </h3>
            <p className="mt-2 break-keep text-sm font-semibold leading-6 text-slate-600">
              {activeView.description} 계약·KCP 승인·취소 데이터는 변경하지 않습니다.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs font-black text-slate-700">
            <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-2 tabular-nums">
              계약 {countFormatter.format(contracts.length)}건 로드
            </span>
            <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-2 tabular-nums">
              결제 {paymentMeta.loaded ? `${countFormatter.format(paymentMeta.total)}건` : "미조회"}
            </span>
            <span
              className={`rounded-full border px-3 py-2 ${
                actionTypeCount > 0
                  ? "border-rose-200 bg-rose-50 text-rose-800"
                  : "border-emerald-200 bg-emerald-50 text-emerald-800"
              }`}
            >
              {actionTypeCount > 0 ? `조치 항목 ${actionTypeCount}종` : "즉시 조치 없음"}
            </span>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-5" aria-label="통합 현황 보기 선택">
          {VIEW_OPTIONS.map((option, index) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setView(option.value)}
              aria-pressed={view === option.value}
              className={`inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl border px-3 py-2 text-sm font-black transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2 ${
                index === VIEW_OPTIONS.length - 1 ? "col-span-2 sm:col-span-1" : ""
              } ${
                view === option.value
                  ? option.activeClass
                  : option.inactiveClass
              }`}
            >
              <span
                aria-hidden="true"
                className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                  view === option.value ? "bg-white" : option.markerClass
                }`}
              />
              {option.label}
            </button>
          ))}
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200/80 bg-white p-4 shadow-sm sm:p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h4 className="text-base font-black text-slate-950">조회 조건</h4>
            <p className="mt-1 text-xs font-semibold text-slate-600">
              필요한 범위만 선택해 숫자와 상태를 빠르게 좁혀보세요.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-slate-600">
            <span className="rounded-full bg-slate-100 px-3 py-2" aria-live="polite">
              마지막 갱신 {refreshedAtLabel}
            </span>
            <button
              type="button"
              onClick={resetFilters}
              className="min-h-[44px] rounded-xl border border-slate-200 bg-white px-4 py-2 font-black text-slate-700 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
            >
              필터 초기화
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 min-[390px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <label className="min-w-0 text-xs font-black text-slate-700">
            기간
            <select
              aria-label="대시보드 기간"
              value={filters.period}
              onChange={(event) => updatePeriod(event.target.value as DashboardPeriod)}
              className="mt-1 min-h-[44px] w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-800 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
            >
              {PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className="min-w-0 text-xs font-black text-slate-700">
            법인
            <select
              aria-label="대시보드 법인"
              value={filters.corporation}
              onChange={(event) => setFilters((current) => ({ ...current, corporation: event.target.value }))}
              className="mt-1 min-h-[44px] w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-800 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
            >
              <option value="ALL">전체 법인</option>
              {dashboard.filterOptions.corporations.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </label>
          <label className="min-w-0 text-xs font-black text-slate-700">
            담당자
            <select
              aria-label="대시보드 담당자"
              value={filters.manager}
              onChange={(event) => setFilters((current) => ({ ...current, manager: event.target.value }))}
              className="mt-1 min-h-[44px] w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-800 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
            >
              <option value="ALL">전체 담당자</option>
              {dashboard.filterOptions.managers.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </label>
          <label className="min-w-0 text-xs font-black text-slate-700">
            파트
            <select
              aria-label="대시보드 파트"
              value={filters.part}
              onChange={(event) => setFilters((current) => ({ ...current, part: event.target.value }))}
              className="mt-1 min-h-[44px] w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-800 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
            >
              <option value="ALL">전체 파트</option>
              {dashboard.filterOptions.parts.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </label>
          <label className="min-w-0 text-xs font-black text-slate-700">
            상태
            <select
              aria-label="대시보드 상태"
              value={filters.status}
              onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value as DashboardStatusFilter }))}
              className="mt-1 min-h-[44px] w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-800 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
            >
              {STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
          <button
            type="button"
            onClick={reloadDashboardPayments}
            disabled={paymentLoading}
            className="min-h-[44px] self-end rounded-xl bg-violet-700 px-3 text-sm font-black text-white shadow-sm transition hover:bg-violet-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2 disabled:cursor-wait disabled:bg-slate-300"
          >
            {paymentLoading ? "갱신 중..." : "결제 새로고침"}
          </button>
        </div>
        {filters.period === "CUSTOM" ? (
          <div className="mt-3 grid grid-cols-1 gap-3 min-[390px]:grid-cols-2 sm:max-w-md">
            <label className="min-w-0 text-xs font-black text-slate-700">
              시작일
              <input type="date" value={filters.dateFrom} onChange={(event) => updateCustomDate("dateFrom", event.target.value)} className="mt-1 min-h-[44px] w-full min-w-0 rounded-xl border border-slate-200 px-3 text-sm font-bold focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100" />
            </label>
            <label className="min-w-0 text-xs font-black text-slate-700">
              종료일
              <input type="date" value={filters.dateTo} onChange={(event) => updateCustomDate("dateTo", event.target.value)} className="mt-1 min-h-[44px] w-full min-w-0 rounded-xl border border-slate-200 px-3 text-sm font-bold focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100" />
            </label>
          </div>
        ) : null}
        <div className="mt-4 flex flex-wrap gap-2 text-xs font-bold text-slate-700" aria-label="현재 적용된 필터">
          <span className="rounded-full bg-violet-50 px-3 py-2 text-violet-800">기간: {activePeriodLabel}</span>
          <span className="rounded-full bg-slate-100 px-3 py-2">법인: {filters.corporation === "ALL" ? "전체" : filters.corporation}</span>
          <span className="rounded-full bg-slate-100 px-3 py-2">담당자: {filters.manager === "ALL" ? "전체" : filters.manager}</span>
          <span className="rounded-full bg-slate-100 px-3 py-2">파트: {filters.part === "ALL" ? "전체" : filters.part}</span>
          <span className="rounded-full bg-slate-100 px-3 py-2">상태: {activeStatusLabel}</span>
          {filters.part !== "ALL" ? (
            <span className="rounded-full bg-amber-100 px-3 py-2 text-amber-900">결제에 파트 필드가 없어 파트 필터는 계약에만 적용</span>
          ) : null}
        </div>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold text-slate-500">
          <span>{filters.dateFrom} ~ {filters.dateTo}</span>
          <span>요청액=요청일 · 승인액=승인일</span>
          <span>만료=조회 범위 내 미승인 BUYLINK 현재 상태</span>
        </div>
      </section>

      {renderPaymentState()}

      {!hasDashboardData && !paymentLoading && view !== "EXCEPTIONS" ? (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-5 py-12 text-center shadow-sm" role="status">
          <p className="font-black text-slate-700">선택한 조건에 해당하는 데이터가 없습니다.</p>
          <p className="mt-2 text-sm font-bold text-slate-500">기간 또는 법인·담당자·상태 필터를 확인해 주세요.</p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-5 min-h-[44px] rounded-xl bg-violet-700 px-5 py-2 text-sm font-black text-white transition hover:bg-violet-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
          >
            기본 조건으로 보기
          </button>
        </div>
      ) : (
        <div role="region" aria-label={`${activeView.label} 내용`}>
          {view === "OVERVIEW"
            ? renderOverview()
            : view === "CONTRACTS"
            ? renderContracts()
            : view === "PAYMENTS"
            ? renderPayments()
            : view === "MANAGERS"
            ? renderManagers()
            : renderExceptions()}
        </div>
      )}
    </div>
  );
}
